import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import Form from 'react-bootstrap/Form'
import classnames from 'classnames'
import { useDebounce } from 'use-debounce'

import { Button } from '../Button/Button'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import {
  selectFileBrowserBreadcrumbs,
  selectFileBrowserIsInitialLoading,
  selectFileBrowserIsFolderLoading,
  selectFileBrowserIsSearching,
  selectFileBrowserSearchQuery,
  selectFileBrowserDisplayItems,
  selectFileBrowserSelectedIdsSet,
  selectFileBrowserIsInSearchMode,
  selectFileBrowserIsAtRoot,
  selectFileBrowserSelectedItems,
  selectFileBrowserCurrentItems,
  selectFileBrowserTotalCount,
  selectFileBrowserNextCursor,
  selectFileBrowserSearchResults,
  selectFileBrowserSearchTotalCount,
  selectFileBrowserSearchNextCursor,
  setFolderView,
  appendFolderItems,
  appendSearchResults,
  setIsFolderLoading,
  setSearchQuery,
  setSearchResults,
  setIsSearching,
  toggleItemSelection,
  clearSelection,
  selectAllItems,
  clearSearch,
} from '../../store/documentFileBrowser/documentFileBrowserSlice'
import { type WorkspaceNode } from '../../store/documentConnectors/documentConnectors'
import { type IntegrationServiceId } from '../../common/integrations'
import { type FileBrowserItem, type FolderContents, type FolderItemsPage, type SelectionMode, ROOT_ANCHOR_ID } from './types'
import { BreadcrumbBar } from './BreadcrumbBar'
import { FileBrowserRow } from './FileBrowserRow'
import { WorkspaceSelector } from './WorkspaceSelector'
import styles from './FileBrowser.module.scss'

const getModeFlags = (selectionMode: SelectionMode) => {
  const isFolderMode = selectionMode === 'single-folder' || selectionMode === 'multi-folder'
  const isSingleSelect = selectionMode === 'single-file' || selectionMode === 'single-folder'
  const isFileSelectable = !isFolderMode
  const isFolderSelectable = isFolderMode || selectionMode === 'any'
  return { isFolderMode, isSingleSelect, isFileSelectable, isFolderSelectable }
}

export interface FileBrowserProps {
  serviceId: IntegrationServiceId
  onFolderOpen: (folderId: string | null) => Promise<FolderContents>
  onLoadMore: (folderId: string | null, cursor: string) => Promise<FolderItemsPage>
  onSearch: (query: string, currentFolderId: string | null) => Promise<FolderItemsPage>
  onSearchLoadMore: (query: string, currentFolderId: string | null, cursor: string) => Promise<FolderItemsPage>
  workspaces: WorkspaceNode[]
  workspace: string | null
  onWorkspaceChange: (nodeId: string) => void
  isWorkspacesLoading?: boolean
  selectionMode?: SelectionMode
  showOnlyFolders?: boolean
  showDisabledFiles?: boolean
  onSelectionChange?: (selectedItems: FileBrowserItem[]) => void
}

export const FileBrowser = ({
  serviceId,
  onFolderOpen,
  onLoadMore,
  onSearch,
  onSearchLoadMore,
  workspaces,
  workspace,
  onWorkspaceChange,
  isWorkspacesLoading,
  selectionMode = 'multi-file',
  showOnlyFolders,
  showDisabledFiles,
  onSelectionChange,
}: FileBrowserProps) => {
  const dispatch = useAppDispatch()

  const breadcrumbs = useAppSelector(selectFileBrowserBreadcrumbs)
  const isInitialLoading = useAppSelector(selectFileBrowserIsInitialLoading)
  const isFolderLoading = useAppSelector(selectFileBrowserIsFolderLoading)
  const isSearching = useAppSelector(selectFileBrowserIsSearching)
  const searchQuery = useAppSelector(selectFileBrowserSearchQuery)
  const sortedItems = useAppSelector(selectFileBrowserDisplayItems)
  const selectedIds = useAppSelector(selectFileBrowserSelectedIdsSet)
  const selectedItems = useAppSelector(selectFileBrowserSelectedItems)
  const isInSearchMode = useAppSelector(selectFileBrowserIsInSearchMode)
  const isAtRoot = useAppSelector(selectFileBrowserIsAtRoot)
  const currentItems = useAppSelector(selectFileBrowserCurrentItems)
  const totalCount = useAppSelector(selectFileBrowserTotalCount)
  const nextCursor = useAppSelector(selectFileBrowserNextCursor)
  const searchResults = useAppSelector(selectFileBrowserSearchResults)
  const searchTotalCount = useAppSelector(selectFileBrowserSearchTotalCount)
  const searchNextCursor = useAppSelector(selectFileBrowserSearchNextCursor)

  const [debouncedQuery] = useDebounce(searchQuery, 300)
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
  const prevSelectedKeyRef = useRef('')
  const folderCacheRef = useRef(new Map<string | null, FolderContents>())

  const { isFolderMode, isSingleSelect, isFileSelectable, isFolderSelectable } = getModeFlags(selectionMode)

  const isSingleFolder = selectionMode === 'single-folder'
  const isFolderPicker = showOnlyFolders || showDisabledFiles || isSingleFolder
  const displayItems = showOnlyFolders ? sortedItems.filter((item) => item.type === 'folder') : sortedItems
  const showCheckboxes = !isFolderPicker

  const currentFolderId = isAtRoot ? null : (breadcrumbs[breadcrumbs.length - 1]?.id ?? null)

  const selectionNoun = isFolderMode ? 'folder' : selectionMode === 'any' ? 'item' : 'file'
  const selectionCountText =
    selectedItems.length === 0 ? `No ${selectionNoun} selected` : `${selectedItems.length} ${selectionNoun}${selectedItems.length !== 1 ? 's' : ''} selected`

  const allSelectableIds = useMemo(
    () => displayItems.filter((item) => (item.type === 'file' && isFileSelectable) || (item.type === 'folder' && isFolderSelectable)).map((item) => item.id),
    [displayItems, isFileSelectable, isFolderSelectable],
  )
  const anySelected = selectedItems.length > 0

  const handleSelectAll = useCallback(() => {
    if (anySelected) {
      dispatch(clearSelection())
    } else {
      dispatch(selectAllItems(allSelectableIds))
    }
  }, [anySelected, allSelectableIds, dispatch])

  useEffect(() => {
    if (!onSelectionChange) {
      return
    }
    const key = selectedItems.map((item) => item.id).join(',')
    if (key !== prevSelectedKeyRef.current) {
      prevSelectedKeyRef.current = key
      onSelectionChange(selectedItems)
    }
  }, [selectedItems, onSelectionChange])

  useEffect(() => {
    const trimmedQuery = debouncedQuery.trim()

    if (!trimmedQuery) {
      dispatch(setSearchResults(null))
      return
    }

    const lastCrumb = breadcrumbs[breadcrumbs.length - 1]
    const searchFolderId = lastCrumb.id === ROOT_ANCHOR_ID ? null : lastCrumb.id

    const controller = new AbortController()

    const runSearch = async () => {
      dispatch(setIsSearching(true))
      try {
        const results = await onSearch(trimmedQuery, searchFolderId)
        if (!controller.signal.aborted) {
          dispatch(setSearchResults(results))
        }
      } catch {
        if (!controller.signal.aborted) {
          dispatch(addToast({ variant: 'error', title: 'Search failed', messages: ['Please try again.'] }))
        }
      } finally {
        if (!controller.signal.aborted) {
          dispatch(setIsSearching(false))
        }
      }
    }

    runSearch()

    return () => {
      controller.abort()
    }
  }, [breadcrumbs, debouncedQuery, dispatch, onSearch])

  const handleClearSearch = useCallback(() => {
    dispatch(clearSearch())
  }, [dispatch])

  const openFolder = useCallback(
    async (folderId: string | null) => {
      dispatch(clearSearch())
      const cached = folderId === null ? undefined : folderCacheRef.current.get(folderId)
      if (cached) {
        dispatch(setFolderView(cached))
        return
      }
      dispatch(setIsFolderLoading(true))
      try {
        const contents = await onFolderOpen(folderId)
        if (folderId !== null) {
          folderCacheRef.current.set(folderId, contents)
        }
        dispatch(setFolderView(contents))
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Failed to load folder', messages: ['Please try again.'] }))
      } finally {
        dispatch(setIsFolderLoading(false))
      }
    },
    [dispatch, onFolderOpen],
  )

  const handleNavigateToFolder = useCallback((folderId: string) => openFolder(folderId), [openFolder])

  const shownCount = isInSearchMode ? (searchResults?.length ?? 0) : currentItems.length
  const activeTotalCount = isInSearchMode ? searchTotalCount : totalCount
  const activeNextCursor = isInSearchMode ? searchNextCursor : nextCursor

  const handleLoadMore = useCallback(async () => {
    if (!activeNextCursor) {
      return
    }
    setIsLoadingMore(true)
    try {
      if (isInSearchMode) {
        const page = await onSearchLoadMore(debouncedQuery.trim(), currentFolderId, activeNextCursor)
        dispatch(appendSearchResults(page))
      } else {
        const page = await onLoadMore(currentFolderId, activeNextCursor)
        dispatch(appendFolderItems(page))
        const cached = currentFolderId === null ? undefined : folderCacheRef.current.get(currentFolderId)
        if (currentFolderId && cached) {
          folderCacheRef.current.set(currentFolderId, {
            ...cached,
            items: [...cached.items, ...page.items],
            totalCount: page.totalCount ?? cached.totalCount,
            nextCursor: page.nextCursor ?? null,
          })
        }
      }
    } catch {
      dispatch(addToast({ variant: 'error', title: 'Failed to load more items', messages: ['Please try again.'] }))
    } finally {
      setIsLoadingMore(false)
    }
  }, [dispatch, onLoadMore, onSearchLoadMore, isInSearchMode, debouncedQuery, currentFolderId, activeNextCursor])

  const handleNavigateToBreadcrumb = useCallback(
    (index: number) => {
      if (index === breadcrumbs.length - 1) {
        return
      }
      openFolder(breadcrumbs[index].id === ROOT_ANCHOR_ID ? null : breadcrumbs[index].id)
    },
    [breadcrumbs, openFolder],
  )

  const selectItem = useCallback(
    (itemId: string) => {
      dispatch(toggleItemSelection({ itemId, singleSelect: isSingleSelect }))
    },
    [dispatch, isSingleSelect],
  )

  const handleRowClick = useCallback(
    (item: FileBrowserItem) => {
      const isFolder = item.type === 'folder'
      if (isFolder && !isFolderPicker && isFolderSelectable) {
        selectItem(item.id)
        return
      }
      if (isFolder) {
        handleNavigateToFolder(item.id)
        return
      }
      if (isFileSelectable) {
        selectItem(item.id)
      }
    },
    [isFolderPicker, isFolderSelectable, isFileSelectable, selectItem, handleNavigateToFolder],
  )

  const handleRowDoubleClick = useCallback(
    (item: FileBrowserItem) => {
      if (item.type === 'folder' && isFolderSelectable) {
        handleNavigateToFolder(item.id)
      }
    },
    [isFolderSelectable, handleNavigateToFolder],
  )

  if (isInitialLoading || isWorkspacesLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center h-100'>
        <Spinner />
      </div>
    )
  }

  const checkType = isSingleSelect ? 'radio' : 'checkbox'

  return (
    <div className={styles.container}>
      <div className={styles.controlsRow}>
        {workspaces.length > 0 ? (
          <div className={styles.workspaceControl}>
            <WorkspaceSelector serviceId={serviceId} workspaces={workspaces} value={workspace} onChange={onWorkspaceChange} />
          </div>
        ) : null}
        <div className={styles.searchBox}>
          <i className='bi bi-search zocks-text-gray-400' />
          <Form.Control
            type='text'
            placeholder={isFolderPicker ? 'Search folders' : 'Search files and folders'}
            size='sm'
            className={classnames(styles.searchInput, 'border-0 p-0 bg-transparent shadow-none')}
            value={searchQuery}
            onChange={(e) => dispatch(setSearchQuery(e.target.value))}
          />
          {isSearching ? (
            <Spinner size='sm' />
          ) : searchQuery ? (
            <Button variant='borderless' className={styles.clearSearchButton} onClick={handleClearSearch} aria-label='Clear search input'>
              <i className='bi bi-x-lg' />
            </Button>
          ) : null}
        </div>
      </div>

      {isInSearchMode ? (
        <div className={classnames(styles.searchResultsHeader, 'paragraph-small')}>
          <i className='bi bi-search' />
          <span>
            Search results for <strong>&quot;{debouncedQuery}&quot;</strong>
          </span>
          {showCheckboxes ? <span className={classnames(styles.selectionCount, 'ms-auto paragraph-small')}>{selectionCountText}</span> : null}
        </div>
      ) : (
        <div className={styles.breadcrumbRow}>
          <BreadcrumbBar breadcrumbs={breadcrumbs} onNavigate={handleNavigateToBreadcrumb} />
          {showCheckboxes ? <span className={classnames(styles.selectionCount, 'ms-auto paragraph-small')}>{selectionCountText}</span> : null}
        </div>
      )}

      <div className={classnames(styles.headerRow, 'lead-small')}>
        <span className='flex-fill'>NAME</span>
        <span className={styles.colUpdated}>UPDATED</span>
        <span className={styles.colSize}>SIZE</span>
        {showCheckboxes ? (
          isSingleSelect ? (
            <span className={styles.colCheckbox} />
          ) : (
            <span className={styles.colCheckbox}>
              <Form.Check type='checkbox' checked={anySelected} onChange={handleSelectAll} aria-label='Select all' className='flex-shrink-0 m-0' />
            </span>
          )
        ) : (
          <span className={styles.colChevron} />
        )}
      </div>

      <div className={styles.listBody}>
        {isFolderLoading ? (
          <div className='d-flex align-items-center justify-content-center py-5'>
            <Spinner size='sm' className='me-2' />
            <span className='paragraph-small zocks-text-gray-500'>Loading…</span>
          </div>
        ) : displayItems.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center py-5 gap-2'>
            {isFolderPicker ? (
              <>
                <i className={classnames('bi bi-folder zocks-text-gray-400', styles.emptyStateIcon)} />
                <span className='paragraph-small zocks-text-gray-500'>No subfolders here</span>
              </>
            ) : (
              <span className='paragraph-small zocks-text-gray-500'>{isInSearchMode ? 'No results found' : 'This folder is empty'}</span>
            )}
          </div>
        ) : (
          <>
            {displayItems.map((item) => {
              const isFolder = item.type === 'folder'
              const isDisabled = !isFolder && (isFolderPicker || isFolderMode)
              return (
                <FileBrowserRow
                  key={item.id}
                  item={item}
                  isSelected={selectedIds.has(item.id)}
                  isDisabled={isDisabled}
                  showCheckboxes={showCheckboxes}
                  isFolderSelectable={isFolderSelectable}
                  isFileSelectable={isFileSelectable}
                  checkType={checkType}
                  onRowClick={handleRowClick}
                  onRowDoubleClick={handleRowDoubleClick}
                  onSelect={selectItem}
                  onOpenFolder={handleNavigateToFolder}
                />
              )
            })}
            {activeNextCursor ? (
              <div className={styles.loadMoreRow}>
                <span className='paragraph-small zocks-text-gray-700'>
                  {activeTotalCount != null ? `${shownCount} of ${activeTotalCount} files shown` : `${shownCount} file${shownCount !== 1 ? 's' : ''} shown`}
                </span>
                <Button variant='secondaryGhostButton' onClick={handleLoadMore} loading={isLoadingMore}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
